import React, { useEffect, useMemo, useRef, useState } from "react";

// Responsive width breakpoints — match OptimizedImage so caches stay
// shared between the two components. Most callers render at <50% vw on
// desktop and 100% vw on mobile, so 480..1920 covers the realistic
// device range without bloating srcset.
const DEFAULT_WIDTHS = [320, 480, 640, 768, 1024, 1280, 1600, 1920];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Bunny CDN base (Pull Zone). When set, both /api/files/ and external
// hosts (customer-assets, etc.) get rewritten to go through Bunny
// edge + Optimizer. Bunny Edge Rules handle the origin switch for
// non-`/api/files/` paths (e.g. /job_* → customer-assets).
const CDN_BASE_URL = process.env.REACT_APP_CDN_BASE_URL || "";

// Strip host + query so we get the bare path Bunny needs.
const toCdnPath = (url) => {
  if (!url) return "";
  if (url.startsWith("/")) return url.split("?")[0];
  try {
    return new URL(url).pathname;
  } catch {
    return url.split("?")[0];
  }
};

const buildCdnSrcset = (url, format) => {
  const path = toCdnPath(url);
  return DEFAULT_WIDTHS
    .map(
      (w) =>
        `${CDN_BASE_URL}${path}?width=${w}&format=${format || "webp"}&quality=72 ${w}w`
    )
    .join(", ");
};

/**
 * Build a srcset for the given URL. Returns null when we don't know
 * how to transform the URL — caller should just emit a plain <img src>.
 *
 * Three supported origins:
 *   1. /api/files/...  → our own CMS file route accepts ?w= & ?fmt=
 *   2. images.unsplash.com → built-in `auto=format` + `w=` params
 *   3. customer-assets.emergentagent.com → /api/img proxy on our backend
 *
 * The `format` arg selects a Pillow-side output format ("webp", "avif")
 * or `null` to use the upstream-default (auto-negotiated via Accept).
 */
const buildSrcset = (src, format) => {
  if (!src || typeof src !== "string") return null;
  // CMS file route (relative or absolute)
  if (src.startsWith("/api/files/") || src.includes("/api/files/")) {
    if (CDN_BASE_URL) {
      return buildCdnSrcset(src, format);
    }
    const base = src.split("?")[0];
    return DEFAULT_WIDTHS
      .map((w) => `${base}?w=${w}&fmt=${format || "webp"}&q=72 ${w}w`)
      .join(", ");
  }
  if (src.includes("images.unsplash.com")) {
    try {
      const u = new URL(src);
      ["w", "h", "q", "fm", "auto", "dpr"].forEach((p) => u.searchParams.delete(p));
      if (format === "avif" || format === "webp") {
        u.searchParams.set("fm", format);
      } else {
        u.searchParams.set("auto", "format");
      }
      u.searchParams.set("q", "72");
      return DEFAULT_WIDTHS
        .map((w) => {
          const v = new URL(u.toString());
          v.searchParams.set("w", String(w));
          return `${v.toString()} ${w}w`;
        })
        .join(", ");
    } catch {
      return null;
    }
  }
  if (src.includes("customer-assets.emergentagent.com")) {
    if (CDN_BASE_URL) {
      // Bunny Edge Rule (/job_* → customer-assets) routes this for us.
      return buildCdnSrcset(src, format);
    }
    return DEFAULT_WIDTHS
      .map(
        (w) =>
          `${BACKEND_URL}/api/img?url=${encodeURIComponent(src)}&w=${w}&q=72&fmt=${format || "webp"} ${w}w`
      )
      .join(", ");
  }
  return null;
};

// SmartImage — drop-in <img> replacement that:
//   • Reserves space via aspect-ratio (so layout doesn't shift)
//   • Shows a warm shimmer skeleton while loading (NEVER black)
//   • Fades + blur-to-sharp on first paint
//   • Falls back to `fallback` then to a discreet "no image" plate
//   • Default lazy; `priority` → eager + fetchPriority high
//   • Emits responsive srcset so browsers download the smallest variant
//     that fits the viewport, dramatically reducing bandwidth on mobile.
const SmartImage = ({
  src,
  fallback,
  alt = "",
  aspectRatio,
  priority = false,
  sizes,
  className = "",
  imgClassName = "",
  ...rest
}) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src || fallback || null);
  const triedFallback = useRef(false);

  useEffect(() => {
    setLoaded(false);
    setErrored(false);
    triedFallback.current = false;
    setCurrentSrc(src || fallback || null);
  }, [src, fallback]);

  const handleError = () => {
    if (!triedFallback.current && fallback && currentSrc !== fallback) {
      triedFallback.current = true;
      setCurrentSrc(fallback);
      return;
    }
    setErrored(true);
  };

  // Generate per-format srcsets for AVIF and WebP. Browsers pick the
  // first <source> whose MIME type they support. We intentionally do
  // NOT emit a srcSet on the fallback <img> — every modern browser
  // supports <picture>, so the bare `src` is enough for the rare
  // legacy fallback, and avoiding the duplicate srcSet stops some
  // browsers from double-fetching variants.
  const avifSrcset = useMemo(() => buildSrcset(currentSrc, "avif"), [currentSrc]);
  const webpSrcset = useMemo(() => buildSrcset(currentSrc, "webp"), [currentSrc]);
  const defaultSizes =
    sizes ||
    (priority
      ? "100vw"
      : "(min-width: 1280px) 50vw, (min-width: 768px) 75vw, 100vw");

  return (
    <span
      className={`smart-image relative block overflow-hidden ${className}`}
      style={aspectRatio ? { aspectRatio } : undefined}
    >
      {!loaded && !errored && <span className="smart-image-shimmer" aria-hidden="true" />}
      {currentSrc && !errored && (
        <picture>
          {avifSrcset && <source type="image/avif" srcSet={avifSrcset} sizes={defaultSizes} />}
          {webpSrcset && <source type="image/webp" srcSet={webpSrcset} sizes={defaultSizes} />}
          <img
            src={currentSrc}
            alt={alt}
            loading={priority ? "eager" : "lazy"}
            decoding={priority ? "sync" : "async"}
            fetchPriority={priority ? "high" : "auto"}
            onLoad={() => setLoaded(true)}
            onError={handleError}
            className={`smart-image-img ${loaded ? "is-loaded" : ""} ${imgClassName}`}
            {...rest}
          />
        </picture>
      )}
      {errored && (
        <span className="smart-image-placeholder" aria-label="No image">
          —
        </span>
      )}
    </span>
  );
};

export default SmartImage;
